import { Navigate, useOutletContext, useParams } from "react-router-dom";
import { useFeedback } from "../components/FeedbackProvider.jsx";
import useCampaignDetail from "../hooks/useCampaignDetail.js";
import {
  CampaignDetailPage,
  HistoryPage,
  InstructionsPage,
  SetupPage,
  SummaryPage,
  UserManagementPage,
} from "./index.js";

function useCologuardOutletContext() {
  return useOutletContext();
}

export function CologuardSummaryRoute() {
  const {
    campaignState,
    metricsState,
    loadDashboardMetrics,
    loadCampaigns,
  } = useCologuardOutletContext();

  return (
    <SummaryPage
      campaignState={campaignState}
      metricsState={metricsState}
      onRefresh={() => Promise.all([loadCampaigns(), loadDashboardMetrics()])}
    />
  );
}

export function CologuardSetupRoute() {
  const {
    canManageCampaigns,
    form,
    submitError,
    isSubmitting,
    handleFormChange,
    handleGenerateDesignator,
    handleLaunchSubmit,
  } = useCologuardOutletContext();

  if (!canManageCampaigns) {
    return <Navigate to="/cologuard" replace />;
  }

  return (
    <SetupPage
      form={form}
      submitError={submitError}
      isSubmitting={isSubmitting}
      onFormChange={handleFormChange}
      onGenerateDesignator={handleGenerateDesignator}
      onSubmit={handleLaunchSubmit}
    />
  );
}

export function CologuardHistoryRoute() {
  const {
    historyFilters,
    metricsState,
    campaignHistoryState,
    updateHistoryFilter,
    clearHistoryFilters,
    loadCampaignHistory,
    loadDashboardMetrics,
  } = useCologuardOutletContext();

  return (
    <HistoryPage
      historyFilters={historyFilters}
      metricsState={metricsState}
      campaignHistoryState={campaignHistoryState}
      onHistoryFilterChange={updateHistoryFilter}
      onApplyFilters={() => loadCampaignHistory(historyFilters, { pageNumber: 1, force: true })}
      onClearFilters={clearHistoryFilters}
      onHistoryRefresh={() =>
        Promise.all([
          loadCampaignHistory(historyFilters, {
            pageNumber: campaignHistoryState.currentPage,
            force: true,
          }),
          loadDashboardMetrics(),
        ])
      }
      onHistoryPreviousPage={() =>
        loadCampaignHistory(historyFilters, {
          pageNumber: Math.max(1, campaignHistoryState.currentPage - 1),
        })
      }
      onHistoryNextPage={() =>
        loadCampaignHistory(historyFilters, { pageNumber: campaignHistoryState.currentPage + 1 })
      }
      onHistoryPageSelect={(pageNumber) => loadCampaignHistory(historyFilters, { pageNumber })}
    />
  );
}

export function CologuardUsersRoute() {
  const {
    currentUser,
    isAdminUser,
    userState,
    userFilter,
    userForm,
    userFormState,
    setUserFilter,
    loadUsers,
    handleUserFormChange,
    handleUserCreate,
  } = useCologuardOutletContext();

  if (!isAdminUser) {
    return null;
  }

  return (
    <UserManagementPage
      currentUser={currentUser}
      userState={userState}
      userFilter={userFilter}
      userForm={userForm}
      userFormState={userFormState}
      onUserFilterChange={setUserFilter}
      onUserRefresh={loadUsers}
      onUserFormChange={handleUserFormChange}
      onUserCreate={handleUserCreate}
    />
  );
}

export function CologuardInstructionsRoute() {
  const { currentUser } = useCologuardOutletContext();

  return <InstructionsPage currentUser={currentUser} />;
}

export function CologuardCampaignDetailRoute() {
  const { campaignId } = useParams();
  const { accessToken, canManageCampaigns } = useCologuardOutletContext();
  const { notify } = useFeedback();
  const detail = useCampaignDetail({
    campaignId,
    accessToken,
    enabled: Boolean(campaignId),
    notify,
  });

  return (
    <CampaignDetailPage
      {...detail}
      canManageCampaigns={canManageCampaigns}
      onRefresh={detail.loadCampaign}
      onRetry={detail.retryCampaign}
    />
  );
}
